import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Level4Item } from "./Level4Item";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type GrandChildItemProps = {
  grandChild: {
    id: number;
    name: string;
    index: string;
    title?: string;
    description?: string;
    node_1?: string;
    node_2?: string;
    node_3?: string;
    product_count?: string;
    url?: string;
    cat_text?: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
  selectedUpdate: string;
};

export const GrandChildItem = ({ grandChild, isExpanded, onToggle, selectedUpdate }: GrandChildItemProps) => {
  const { data: level4Categories } = useQuery({
    queryKey: ["level4-categories", grandChild.name, isExpanded, selectedUpdate],
    enabled: isExpanded,
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 4)
        .eq("parent_node_name", grandChild.name);

      if (selectedUpdate && selectedUpdate !== "all") {
        const [hour, date] = selectedUpdate.split('h ');
        const [day, month, year] = date.split('-');
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  // Check if there are any child categories
  const { data: hasChildren } = useQuery({
    queryKey: ["has-level4-categories", grandChild.name],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("categories_atp_autoteile_de")
        .select("*", { count: 'exact', head: true })
        .eq("level", 4)
        .eq("parent_node_name", grandChild.name);

      if (error) throw error;
      return count && count > 0;
    },
  });

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
          <div className="w-5">
            {hasChildren && (
              <ChevronRight
                className={`h-5 w-5 transition-transform ${
                  isExpanded ? "rotate-90" : ""
                }`}
              />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="font-medium">
                  {grandChild.url ? (
                    <a 
                      href={grandChild.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {grandChild.name}
                    </a>
                  ) : (
                    grandChild.name
                  )}
                </div>
                <Badge 
                  variant={grandChild.index === "index, follow" ? "default" : "destructive"}
                >
                  {grandChild.index}
                </Badge>
                <Badge
                  variant={grandChild.cat_text ? "default" : "secondary"}
                  className={grandChild.cat_text ? "bg-green-500" : "bg-red-200"}
                >
                  {grandChild.cat_text ? "Found content" : "no content"}
                </Badge>
              </div>
              {grandChild.product_count && (
                <div className="text-sm text-muted-foreground">
                  Products: {grandChild.product_count}
                </div>
              )}
            </div>
            {grandChild.title && (
              <p className="text-sm text-muted-foreground text-left font-medium">
                {grandChild.title}
              </p>
            )}
            {grandChild.description && (
              <p className="text-sm text-muted-foreground text-left">
                {grandChild.description}
              </p>
            )}
            <div className="text-sm text-muted-foreground">
              {(grandChild.node_1 || grandChild.node_2 || grandChild.node_3) && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {grandChild.node_1 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{grandChild.node_1}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(grandChild.node_2 || grandChild.node_3) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {grandChild.node_2 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{grandChild.node_2}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {grandChild.node_3 && <BreadcrumbSeparator />}
                      </>
                    )}
                    {grandChild.node_3 && (
                      <BreadcrumbItem>
                        <BreadcrumbLink href="#">{grandChild.node_3}</BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 space-y-2 mt-2">
          {level4Categories?.map((level4Item) => (
            <Level4Item
              key={level4Item.id}
              item={level4Item}
              isExpanded={false}
              onToggle={() => {}}
              selectedUpdate={selectedUpdate}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};